import { useMemo, useState } from 'react';
import { UsersRound } from 'lucide-react';
import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import {
  useAssignKnowledgeGroupMutation,
  useUnassignKnowledgeGroupMutation,
  useUpdateKnowledgeGroupAssignmentRoleMutation,
} from '../../features/knowledge/mutations/useKnowledgeGroupAssignmentMutations';
import { useKnowledgeGroupAssignmentsQuery } from '../../features/knowledge/queries/useKnowledgeGroupAssignmentsQuery';
import { useTenantGroupsQuery } from '../../features/tenant/queries/useTenantGroupsQuery';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type {
  KnowledgeBaseGroupAssignment,
  KnowledgeMemberRole,
  WithResourceId,
} from '../../types/knowledge';
import { KnowledgeDatabaseGroupAssignForm } from './KnowledgeDatabaseGroupAssignForm';
import { KnowledgeDatabaseGroupAssignmentRow } from './KnowledgeDatabaseGroupAssignmentRow';
import './KnowledgeDatabaseGroupAssignments.css';

const MEMBER_ROLES: KnowledgeMemberRole[] = ['ADMIN', 'EDITOR', 'VIEWER'];

type KnowledgeDatabaseGroupAssignmentsProps = {
  databaseId: string;
  canManage: boolean;
};

export function KnowledgeDatabaseGroupAssignments({
  databaseId,
  canManage,
}: KnowledgeDatabaseGroupAssignmentsProps) {
  const { translate } = useI18n();
  const assignmentsQuery = useKnowledgeGroupAssignmentsQuery(databaseId);
  const groupsQuery = useTenantGroupsQuery({ enabled: true });
  const assignMutation = useAssignKnowledgeGroupMutation();
  const updateRoleMutation = useUpdateKnowledgeGroupAssignmentRoleMutation();
  const unassignMutation = useUnassignKnowledgeGroupMutation();

  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedRole, setSelectedRole] =
    useState<KnowledgeMemberRole>('VIEWER');
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<WithResourceId<KnowledgeBaseGroupAssignment> | null>(null);

  const groupsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groupsQuery.data ?? []) {
      map.set(group.id, group.name);
    }
    return map;
  }, [groupsQuery.data]);

  const assignedGroupIds = useMemo(() => {
    const ids = new Set<string>();
    for (const assignment of assignmentsQuery.data ?? []) {
      if (assignment.userGroupId) {
        ids.add(assignment.userGroupId);
      }
    }
    return ids;
  }, [assignmentsQuery.data]);

  const availableGroupOptions = useMemo(
    () =>
      (groupsQuery.data ?? [])
        .filter((group) => !assignedGroupIds.has(group.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((group) => ({ value: group.id, label: group.name })),
    [assignedGroupIds, groupsQuery.data],
  );

  const roleOptions = useMemo(
    () =>
      MEMBER_ROLES.map((role) => ({
        value: role,
        label: translate(`knowledge.members.roles.${role}`),
      })),
    [translate],
  );

  const assignments = assignmentsQuery.data ?? [];

  return (
    <section className="knowledge-group-assignments">
      <div className="knowledge-group-assignments__header">
        <div className="knowledge-group-assignments__heading">
          <UsersRound
            className="knowledge-group-assignments__icon"
            strokeWidth={2}
            aria-hidden
          />
          <div>
            <h3 className="knowledge-group-assignments__title">
              {translate('knowledge.members.groupsHeading')}
            </h3>
            <p className="knowledge-group-assignments__description">
              {translate('knowledge.members.groupsDescription')}
            </p>
          </div>
        </div>
      </div>

      {assignmentsQuery.isLoading || groupsQuery.isLoading ? (
        <p className="knowledge-group-assignments__status">
          {translate('knowledge.members.groupsLoading')}
        </p>
      ) : null}

      {assignmentsQuery.isError || groupsQuery.isError ? (
        <p className="knowledge-group-assignments__status" role="alert">
          {translate('knowledge.members.groupsLoadError')}
        </p>
      ) : null}

      {canManage ? (
        <KnowledgeDatabaseGroupAssignForm
          selectedGroupId={selectedGroupId}
          selectedRole={selectedRole}
          availableGroupOptions={availableGroupOptions}
          roleOptions={roleOptions}
          pendingKey={pendingKey}
          translate={translate}
          onSelectedGroupIdChange={setSelectedGroupId}
          onSelectedRoleChange={(value) => {
            if (MEMBER_ROLES.includes(value)) {
              setSelectedRole(value);
            }
          }}
          onAssign={() => {
            if (selectedGroupId === '') {
              return;
            }
            const userGroupId = selectedGroupId;
            setPendingKey(userGroupId);
            assignMutation.mutate(
              {
                databaseId,
                userGroupId,
                role: selectedRole,
              },
              {
                onSuccess: () => {
                  toast.success({
                    title: translate(
                      'knowledge.members.assignGroupSuccessTitle',
                    ),
                    description: '',
                  });
                  setSelectedGroupId('');
                },
                onError: (error) => {
                  void toastMutationError(
                    error,
                    translate('knowledge.members.assignGroupErrorTitle'),
                  );
                },
                onSettled: () => setPendingKey(null),
              },
            );
          }}
        />
      ) : null}

      {!assignmentsQuery.isLoading &&
      !assignmentsQuery.isError &&
      assignments.length === 0 ? (
        <p className="knowledge-group-assignments__status">
          {translate('knowledge.members.groupsEmpty')}
        </p>
      ) : null}

      {assignments.length > 0 ? (
        <ul className="knowledge-group-assignments__list">
          {assignments.map((assignment) => {
            if (
              typeof assignment.id !== 'string' ||
              !assignment.userGroupId ||
              !assignment.role
            ) {
              return null;
            }
            const groupName =
              groupsById.get(assignment.userGroupId) ??
              translate('knowledge.members.unknownGroup');
            const isPending = pendingKey === assignment.id;

            return (
              <KnowledgeDatabaseGroupAssignmentRow
                key={assignment.id}
                assignment={{
                  ...assignment,
                  id: assignment.id,
                  userGroupId: assignment.userGroupId,
                  role: assignment.role,
                }}
                groupName={groupName}
                canManage={canManage}
                isPending={isPending}
                roleOptions={roleOptions}
                memberRoles={MEMBER_ROLES}
                translate={translate}
                onRoleChange={(role) => {
                  setPendingKey(assignment.id);
                  updateRoleMutation.mutate(
                    {
                      databaseId,
                      assignment,
                      role,
                    },
                    {
                      onError: (error) => {
                        void toastMutationError(
                          error,
                          translate('knowledge.members.groupRoleErrorTitle'),
                        );
                      },
                      onSettled: () => setPendingKey(null),
                    },
                  );
                }}
                onRequestRemove={() => setDeleteTarget(assignment)}
              />
            );
          })}
        </ul>
      ) : null}

      <LiksConfirmDialog
        isOpen={deleteTarget != null}
        title={translate('knowledge.members.removeGroupTitle')}
        message={translate('knowledge.members.removeGroupMessage', {
          name:
            deleteTarget?.userGroupId != null
              ? (groupsById.get(deleteTarget.userGroupId) ??
                translate('knowledge.members.unknownGroup'))
              : translate('knowledge.members.unknownGroup'),
        })}
        confirmLabel={translate('knowledge.members.removeLabel')}
        cancelLabel={translate('knowledge.deleteCancel')}
        onCancel={() => {
          if (!unassignMutation.isPending) {
            setDeleteTarget(null);
          }
        }}
        onConfirm={() => {
          if (!deleteTarget?.id || unassignMutation.isPending) {
            return;
          }
          unassignMutation.mutate(
            {
              databaseId,
              assignmentId: deleteTarget.id,
            },
            {
              onSuccess: () => {
                toast.success({
                  title: translate('knowledge.members.removeGroupSuccessTitle'),
                  description: '',
                });
                setDeleteTarget(null);
              },
              onError: (error) => {
                void toastMutationError(
                  error,
                  translate('knowledge.deleteErrorTitle'),
                  translate('knowledge.deleteErrorDescription'),
                );
              },
            },
          );
        }}
      />
    </section>
  );
}
